import React from 'react'
import { Modal, ModalHeader, ModalBody, Collapse, ModalFooter } from 'reactstrap'
import { I18n, Translate } from 'react-redux-i18n'
import { BondTradingPane } from '../types'
import { formatCurrency } from '../lib/currency-formatter'
import Spinner from './Spinner'
import {
  calculatePrincipalAmountFromEnteredAmount,
  calculateRequiredFunds,
  TradingAccount,
  BondConfigItem,
} from '@bdswiss/mt4-connector'
import HelpTooltip from './HelpTooltip'
import { notifyParentSignupRequired } from '../utils/common-utils'

export interface Props {
  bondsTradingPane: BondTradingPane
  selectedBond?: BondConfigItem
  account: TradingAccount
  demo?: string
  onCloseDialog: () => void
  buyBond: (symbol: string, volume: number) => void
}

export interface State {
  collapseOpen: { [key: string]: boolean }
}

class BondDetailDialog extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.toggleCollapse = this.toggleCollapse.bind(this)
    this.isOpen = this.isOpen.bind(this)
    this.renderCollapserCode = this.renderCollapserCode.bind(this)

    this.state = {
      collapseOpen: {},
    }
  }

  public render(): React.ReactNode {
    if (!this.props.account) {
      console.log('Bonds Trading pane loading...')
      return <Spinner />
    }

    const {
      showBondDetailDialog,
      rate,
      amount,
      bondBuyMode,
      tradeOperationEnabled,
    } = this.props.bondsTradingPane
    const selectedBond = this.props.selectedBond
    const { account, demo, buyBond } = this.props

    if (!selectedBond) {
      return <span />
    }

    const principalAmount = calculatePrincipalAmountFromEnteredAmount(
      selectedBond,
      amount,
      rate,
      bondBuyMode
    )
    const amountForOrder = Math.floor(principalAmount)
    const accountCurrency = account.currency
    const cashToOpenPosition = calculateRequiredFunds(selectedBond, principalAmount)
    // const daysSinceLastPayment = calculateDaysSinseLastPayment(selectedBond);
    // let's calculate it based on accrued interest
    const daysSinceLastPayment = Math.floor(
      ((selectedBond.accruedInterest * 365) /
        selectedBond.faceValue /
        selectedBond.couponInterestRate) *
        100
    )
    const EURIBOR = 0
    // const leveragedAccuredInterest = daysSinceLastPayment/365*principalAmount*selectedBond.couponInterestRate/100;
    const leveragedAccuredInterest =
      (selectedBond.accruedInterest * principalAmount) / selectedBond.faceValue
    const totalPositionValue =
      (cashToOpenPosition - leveragedAccuredInterest) * selectedBond.leverage
    const marginUsed = cashToOpenPosition - leveragedAccuredInterest
    const marginFinancingRate = EURIBOR + 3
    const totalTradeFinancingCost = ((totalPositionValue - marginUsed) * marginFinancingRate) / 100
    const totalPrincipalDebtAmount = (totalPositionValue / selectedBond.ask) * 100
    const annualInterestEarnings =
      (totalPrincipalDebtAmount * selectedBond.couponInterestRate) / 100
    const netInterestEarnings = annualInterestEarnings - totalTradeFinancingCost

    return (
      <Modal
        className="bond-detail-dialog"
        isOpen={showBondDetailDialog}
        toggle={this.props.onCloseDialog}
        backdrop={true}
        autoFocus={true}
      >
        <ModalHeader toggle={this.props.onCloseDialog}>
          <div className="font-weight-medium fz-20">{selectedBond.name}</div>
        </ModalHeader>
        <ModalBody>
          <div>
            <div className="row mb-2">
              <div className="col-12 color-75">
                <Translate value="bdd_line1" />
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-12 fz-15 font-weight-medium">
                {selectedBond.name}({selectedBond.isin})
              </div>
            </div>
            <div className="row mb-2">
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'cashToOpenPosition'} />
                  <HelpTooltip content="cashToOpenPositionDescription" />
                </div>
                <div className="cellFooter">
                  {formatCurrency(cashToOpenPosition, accountCurrency)}
                </div>
              </div>
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'leveragedAccuredInterest'} />
                  <HelpTooltip content="leveragedAccuredInterestDescription" />
                </div>
                <Collapse isOpen={this.isOpen('leveragedAccuredInterest')}>
                  <div className="cellBody formula">
                    <Translate value={'leveragedAccuredInterestFormula'} />
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <th>
                          <Translate value={'leveragedAccuredInterestTable1'} />
                        </th>
                        <td>{daysSinceLastPayment}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'leveragedAccuredInterestTable2'} />
                        </th>
                        <td>{formatCurrency(principalAmount, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'leveragedAccuredInterestTable3'} />
                        </th>
                        <td>{selectedBond.couponInterestRate}%</td>
                      </tr>
                    </tbody>
                  </table>
                </Collapse>
                <div className="cellFooter">
                  {formatCurrency(leveragedAccuredInterest, selectedBond.currency)}
                </div>
                {this.renderCollapserCode('leveragedAccuredInterest')}
              </div>
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'accountLeverage'} />
                  <HelpTooltip
                    content={I18n.t('accountLeverageDescription', {
                      leverage: selectedBond.leverage,
                    })}
                  />
                </div>
                <div className="cellFooter">1:{selectedBond.leverage}</div>
              </div>

              <div className="cell col-lg-3 gray">
                <div className="cellHeader">
                  <Translate value={'totalPositionValue'} />
                  <HelpTooltip content="totalPositionValueDescription" />
                </div>
                <Collapse isOpen={this.isOpen('totalPositionValue')}>
                  <div className="cellBody formula">
                    <Translate value={'totalPositionValueFormula'} />
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <th>
                          <Translate value={'totalPositionValueTable1'} />
                        </th>
                        <td>{formatCurrency(cashToOpenPosition, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'totalPositionValueTable2'} />
                        </th>
                        <td>{formatCurrency(leveragedAccuredInterest, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'totalPositionValueTable3'} />
                        </th>
                        <td>{selectedBond.leverage}</td>
                      </tr>
                    </tbody>
                  </table>
                </Collapse>
                <div className="cellFooter">
                  {formatCurrency(totalPositionValue, selectedBond.currency)}
                </div>
                {this.renderCollapserCode('totalPositionValue')}
              </div>
            </div>
            <div className="row mb-2">
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'marginUsed'} />
                  <HelpTooltip content="marginUsedDescription" />
                </div>
                <Collapse isOpen={this.isOpen('marginUsed')}>
                  <div className="cellBody formula">
                    <Translate value={'marginUsedFormula'} />
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <th>
                          <Translate value={'marginUsedTable1'} />
                        </th>
                        <td>{formatCurrency(cashToOpenPosition, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'marginUsedTable2'} />
                        </th>
                        <td>{formatCurrency(leveragedAccuredInterest, selectedBond.currency)}</td>
                      </tr>
                    </tbody>
                  </table>
                </Collapse>
                <div className="cellFooter">
                  {formatCurrency(marginUsed, selectedBond.currency)}
                </div>
                {this.renderCollapserCode('marginUsed')}
              </div>
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'marginStopOutLevel'} />
                  <HelpTooltip content="marginStopOutLevelDescription" />
                </div>
                <div className="cellFooter">50%</div>
              </div>
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'marginFinancingRate'} />
                  <HelpTooltip content="marginFinancingRateDescription" />
                </div>
                <div className="cellFooter">EURIBOR + {marginFinancingRate.toFixed(2)}%</div>
              </div>
              <div className="cell col-lg-3 gray">
                <div className="cellHeader">
                  <Translate value={'tradeFinancingCost'} />
                  <HelpTooltip content="tradeFinancingCostDescription" />
                </div>
                <Collapse isOpen={this.isOpen('tradeFinancingCost')}>
                  <div className="cellBody formula">
                    <Translate value={'tradeFinancingCostFormula'} />
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <th>
                          <Translate value={'tradeFinancingCostTable1'} />
                        </th>
                        <td>{formatCurrency(totalPositionValue, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'tradeFinancingCostTable2'} />
                        </th>
                        <td>{formatCurrency(marginUsed, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'tradeFinancingCostTable3'} />
                        </th>
                        <td>EURIBOR + {marginFinancingRate.toFixed(2)}%</td>
                      </tr>
                    </tbody>
                  </table>
                </Collapse>
                <div className="cellFooter">
                  {formatCurrency(totalTradeFinancingCost, selectedBond.currency)}&nbsp;/&nbsp;
                  <Translate value={'yr'} />
                </div>
                {this.renderCollapserCode('tradeFinancingCost')}
              </div>
            </div>

            <div className="row mb-2">
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'currentRateTitle'} />
                  <HelpTooltip content="currentRateDescription" />
                </div>
                <div className="cellFooter">
                  {formatCurrency(selectedBond.ask, selectedBond.currency)}
                </div>
              </div>
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'totalDebtAmount'} />
                  <HelpTooltip content="totalDebtAmountDescription" />
                </div>
                <Collapse isOpen={this.isOpen('totalDebtAmount')}>
                  <div className="cellBody formula">
                    <Translate value={'totalDebtAmountFormula'} />
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <th>
                          <Translate value={'totalDebtAmountTable1'} />
                        </th>
                        <td>{formatCurrency(totalPositionValue, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'totalDebtAmountTable2'} />
                        </th>
                        <td>{formatCurrency(selectedBond.ask, selectedBond.currency)}</td>
                      </tr>
                    </tbody>
                  </table>
                </Collapse>
                <div className="cellFooter">
                  {formatCurrency(totalPrincipalDebtAmount, selectedBond.currency)}
                </div>
                {this.renderCollapserCode('totalDebtAmount')}
              </div>
              <div className="cell col-lg-3">
                <div className="cellHeader">
                  <Translate value={'nominalBondInterestCoupon'} />
                  <HelpTooltip content="nominalBondInterestCouponDescription" />
                </div>
                <div className="cellFooter">{selectedBond.couponInterestRate}%</div>
              </div>

              <div className="cell col-lg-3 gray">
                <div className="cellHeader">
                  <Translate value={'annualInterestEarnings'} />
                  <HelpTooltip content="annualInterestEarningsDescription" />
                </div>
                <Collapse isOpen={this.isOpen('annualInterestEarnings')}>
                  <div className="cellBody formula">
                    <Translate value={'annualInterestEarningsFormula'} />
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <th>
                          <Translate value={'annualInterestEarningsTable1'} />
                        </th>
                        <td>{formatCurrency(totalPrincipalDebtAmount, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'annualInterestEarningsTable2'} />
                        </th>
                        <td>{selectedBond.couponInterestRate}%</td>
                      </tr>
                    </tbody>
                  </table>
                </Collapse>
                <div className="cellFooter">
                  {formatCurrency(annualInterestEarnings, selectedBond.currency)}&nbsp;/&nbsp;
                  <Translate value={'yr'} />
                </div>
                {this.renderCollapserCode('annualInterestEarnings')}
              </div>
            </div>
            <div className="row mb-2">
              <div className="cell col-lg-9">&nbsp;</div>
              <div className="cell col-lg-3 green">
                <div className="cellHeader">
                  <Translate value={'netInterestEarnings'} />
                  <HelpTooltip content="netInterestEarningsDescription" />
                </div>
                <Collapse isOpen={this.isOpen('netInterestEarnings')}>
                  <div className="cellBody formula">
                    <Translate value={'netInterestEarningsFormula'} />
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <th>
                          <Translate value={'netInterestEarningsTable1'} />
                        </th>
                        <td>{formatCurrency(annualInterestEarnings, selectedBond.currency)}</td>
                      </tr>
                      <tr>
                        <th>
                          <Translate value={'netInterestEarningsTable2'} />
                        </th>
                        <td>{formatCurrency(totalTradeFinancingCost, selectedBond.currency)}</td>
                      </tr>
                    </tbody>
                  </table>
                </Collapse>
                <div className="cellFooter">
                  {formatCurrency(netInterestEarnings, selectedBond.currency)}&nbsp;/&nbsp;
                  <Translate value="yr" />
                </div>
                {this.renderCollapserCode('netInterestEarnings')}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn gray_button" onClick={this.props.onCloseDialog}>
            <Translate value={'cancel'} />
          </button>
          <button
            className="btn black_button"
            onClick={() => {
              demo ? notifyParentSignupRequired() : buyBond(selectedBond.mt4Symbol, amountForOrder)
              this.props.onCloseDialog()
            }}
            disabled={!tradeOperationEnabled}
          >
            <Translate value={'placeOrder'} />
          </button>
        </ModalFooter>
      </Modal>
    )
  }

  private renderCollapserCode(elName: string) {
    return (
      <div
        onClick={() => {
          this.toggleCollapse(elName)
        }}
        className="show-more-link--label d-flex flex-row align-items-center"
      >
        <div className={'wt-bond-expander ' + (this.isOpen(elName) ? 'open' : '')}>&nbsp;</div>
        {I18n.t(this.isOpen(elName) ? 'less' : 'more')}
      </div>
    )
  }

  private toggleCollapse(elName: string) {
    const clonedState = { ...this.state }
    clonedState.collapseOpen[elName] = !clonedState.collapseOpen[elName]
    this.setState(prevState => clonedState)
  }

  private isOpen(elName: string) {
    return !!this.state.collapseOpen[elName]
  }
}

export default BondDetailDialog
